import type { ThemeMode } from "@/v2-ui/theme/theme.types"
import type { SelectableThemeColor } from "@/v2-ui/theme/theme.baseColors"
import { createThemeExtension } from "@/v2-ui/theme/theme.extension"

// --- Theme mode ---

export const THEME_MODE_OPTIONS: readonly {
  value: ThemeMode
  label: string
}[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
]

// --- Border radius ---

export const BORDER_RADIUS_OPTIONS = [
  { value: 0, label: "None" },
  { value: 0.4, label: "Small" },
  { value: 0.6, label: "Default" },
  { value: 0.75, label: "Large" },
  { value: 1, label: "Full" },
] as const

// --- Theme color ---

export const THEME_COLOR_OPTIONS: readonly {
  value: SelectableThemeColor
  label: string
}[] = [
  { value: "zinc", label: "Zinc" },
  { value: "blue", label: "Blue" },
  { value: "green", label: "Green" },
  { value: "orange", label: "Orange" },
  { value: "red", label: "Red" },
  { value: "rose", label: "Rose" },
  { value: "violet", label: "Violet" },
  { value: "yellow", label: "Yellow" },
]

// --- Font size ---

export const FONT_SIZE_OPTIONS = [
  { value: 12, label: "Extra Small" },
  { value: 13, label: "Small" },
  { value: 14, label: "Default" },
  { value: 15, label: "Large" },
  { value: 16, label: "Extra Large" },
  { value: 17, label: "Huge" },
] as const

// --- Table cell padding ---

export const useTableCellPadding = createThemeExtension({
  cssVar: "--ui-table-cell-padding",
  defaultValue: "default",
  options: {
    extraCompact: "0.15rem",
    compact: "0.25rem",
    default: "0.5rem",
    comfortable: "0.75rem",
    spacious: "1rem",
  },
})

export const TABLE_CELL_PADDING_OPTIONS = [
  { value: "extraCompact" as const, label: "Extra Compact" },
  { value: "compact" as const, label: "Compact" },
  { value: "default" as const, label: "Default" },
  { value: "comfortable" as const, label: "Comfortable" },
  { value: "spacious" as const, label: "Spacious" },
] as const

// --- Page width ---

export const usePageWidth = createThemeExtension({
  cssVar: "--ui-page-max-width",
  defaultValue: "default",
  options: {
    extraCompact: "1000px",
    compact: "1200px",
    default: "1600px",
    wide: "2000px",
    full: "none",
  },
})

export const PAGE_WIDTH_OPTIONS = [
  { value: "extraCompact" as const, label: "Extra Compact" },
  { value: "compact" as const, label: "Compact" },
  { value: "default" as const, label: "Default" },
  { value: "wide" as const, label: "Wide" },
  { value: "full" as const, label: "Full" },
] as const

// --- Border width ---

export const useBorderWidth = createThemeExtension({
  cssVar: "--ui-border-width",
  defaultValue: "thin",
  options: {
    none: "0px",
    thin: "1px",
    medium: "1.5px",
    thick: "2px",
  },
})

export const BORDER_WIDTH_OPTIONS = [
  { value: "none" as const, label: "None" },
  { value: "thin" as const, label: "Thin" },
  { value: "medium" as const, label: "Medium" },
  { value: "thick" as const, label: "Thick" },
] as const

// --- Table striped rows ---

export const useTableStriped = createThemeExtension({
  cssVar: "--ui-table-striped",
  // On by default — kept in sync with the --ui-table-striped value baked into
  // the backoffice :root (styles.css) so the "On" pill reflects what's actually
  // rendered before the user picks anything.
  defaultValue: "on",
  options: {
    off: "0",
    on: "0.25",
  },
})

export const TABLE_STRIPED_OPTIONS = [
  { value: "off" as const, label: "Off" },
  { value: "on" as const, label: "On" },
] as const

// --- Table multi-column sorting ---
//
// When off (the default), clicking a column header sorts by that column alone —
// the behaviour most operators expect. When on, each header click adds another
// tier to a prioritised multi-column sort (1st, 2nd, …), the power-user feature
// that surprised people who only wanted to re-sort.
export const useTableMultiSort = createThemeExtension({
  cssVar: "--ui-table-multi-sort",
  defaultValue: "off",
  options: {
    off: "0",
    on: "1",
  },
})

export const TABLE_MULTI_SORT_OPTIONS = [
  { value: "off" as const, label: "Off" },
  { value: "on" as const, label: "On" },
] as const

export function useIsMultiSortEnabled() {
  const [multiSort] = useTableMultiSort()
  return multiSort === "on"
}

// --- View mode ---

export type ViewMode = "simple" | "advanced"

export const VIEW_MODE_OPTIONS = [
  { value: "simple" as const, label: "Simple" },
  { value: "advanced" as const, label: "Advanced" },
] as const

export const useViewMode = createThemeExtension({
  cssVar: "--ui-view-mode",
  defaultValue: "simple",
  options: {
    simple: "simple",
    advanced: "advanced",
  },
})

export function useIsAdvancedViewMode() {
  const [viewMode] = useViewMode()
  return viewMode === "advanced"
}
